import {
  Spinner
} from "./chunk-DVS3KI33.mjs";
import {
  cn
} from "./chunk-DYSUVPH5.mjs";

// src/components/button/Button.tsx
import React, { cloneElement } from "react";
import { tv } from "tailwind-variants";
var DEFAULT_SIZE = "md";
var buttonVariants = tv({
  base: "inline-flex cursor-pointer items-center justify-center rounded-md align-bottom transition-button duration-300 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-50",
  variants: {
    variant: {
      primary: "bg-action-primary-surface text-action-primary-foreground",
      "primary-alternative": "bg-action-primary-alternative-surface text-action-primary-alternative-foreground",
      tonal: "bg-action-tonal-surface text-action-tonal-foreground",
      secondary: "bg-action-secondary-surface text-action-secondary-foreground",
      outline: "border border-action-outline text-action-outline-foreground",
      "ghost-primary": "bg-action-ghost-primary-surface text-action-ghost-primary-foreground",
      "ghost-secondary": "bg-transparent text-action-ghost-secondary-foreground",
      destructive: "bg-action-destructive-surface text-action-destructive-foreground",
      "link-primary": "bg-transparent text-action-text-primary-foreground",
      "link-secondary": "bg-transparent text-action-secondary-foreground"
    },
    size: {
      sm: "min-h-9 px-4 py-2 my-label-sm",
      md: "min-h-10 px-4 py-2 my-label-md",
      lg: "min-h-12 px-6 py-3 my-label-md"
    },
    fullWidth: {
      true: "w-full"
    },
    isLoading: {
      true: ""
    },
    isDisabled: {
      true: ""
    },
    hasLeftIcon: {
      false: ""
    },
    hasRightIcon: {
      false: ""
    }
  },
  compoundVariants: [
    {
      hasLeftIcon: true,
      class: "gap-2"
    },
    {
      hasRightIcon: true,
      class: "gap-2"
    },
    {
      size: ["sm", "md"],
      hasLeftIcon: true,
      class: "pl-3 pr-4"
    },
    {
      size: ["sm", "md"],
      hasRightIcon: true,
      class: "pl-4 pr-3"
    },
    {
      size: "lg",
      hasLeftIcon: true,
      class: "pl-5 pr-6"
    },
    {
      size: "lg",
      hasRightIcon: true,
      class: "pl-6 pr-5"
    },
    {
      size: ["sm", "md"],
      variant: ["outline"],
      class: "px-[calc(theme('spacing.4')_-_1px)] py-[calc(theme('spacing.2')_-_1px)]"
    },
    {
      size: "lg",
      variant: ["outline"],
      class: "px-[calc(theme('spacing.6')_-_1px)] py-[calc(theme('spacing.3')_-_1px)]"
    },
    {
      size: ["sm", "md"],
      variant: ["outline"],
      hasLeftIcon: true,
      class: "pl-[calc(theme('spacing.3')_-_1px)] pr-[calc(theme('spacing.4')_-_1px)]"
    },
    {
      size: ["sm", "md"],
      variant: ["outline"],
      hasRightIcon: true,
      class: "pl-[calc(theme('spacing.4')_-_1px)] pr-[calc(theme('spacing.3')_-_1px)]"
    },
    {
      size: "lg",
      variant: ["outline"],
      hasLeftIcon: true,
      class: "pl-[calc(theme('spacing.5')_-_1px)] pr-[calc(theme('spacing.6')_-_1px)]"
    },
    {
      size: "lg",
      variant: ["outline"],
      hasRightIcon: true,
      class: "pl-[calc(theme('spacing.6')_-_1px)] pr-[calc(theme('spacing.5')_-_1px)]"
    },
    {
      variant: ["link-primary", "link-secondary"],
      class: "px-0 py-0"
    },
    {
      variant: ["primary"],
      isDisabled: false,
      class: "hover:bg-action-primary-surface-hover"
    },
    {
      variant: ["primary-alternative"],
      isDisabled: false,
      class: "hover:bg-action-primary-alternative-surface-hover"
    },
    {
      variant: ["tonal"],
      isDisabled: false,
      class: "hover:bg-action-tonal-surface-hover"
    },
    {
      variant: ["secondary"],
      isDisabled: false,
      class: "hover:bg-action-secondary-surface-hover"
    },
    {
      variant: ["outline"],
      isDisabled: false,
      class: "hover:border-action-outline-hover hover:shadow-[inset_0_0_0_1px_theme(borderColor.action-outline-hover)]"
    },
    {
      variant: ["ghost-primary"],
      isDisabled: false,
      class: "hover:bg-action-ghost-primary-surface-hover"
    },
    {
      variant: ["ghost-secondary"],
      isDisabled: false,
      class: "hover:bg-action-ghost-secondary-surface-hover"
    },
    {
      variant: ["destructive"],
      isDisabled: false,
      class: "hover:bg-action-destructive-surface-hover"
    },
    {
      variant: ["link-primary", "link-secondary"],
      isDisabled: false,
      class: "hover:underline"
    }
  ],
  defaultVariants: {
    variant: "primary",
    size: DEFAULT_SIZE,
    fullWidth: false,
    isDisabled: true,
    isLoading: false,
    hasLeftIcon: false,
    hasRightIcon: false
  }
});
var iconSizes = {
  sm: { width: 18, height: 18 },
  md: { width: 20, height: 20 },
  lg: { width: 20, height: 20 }
};
var Button = React.forwardRef(
  ({
    children,
    className,
    variant,
    size = DEFAULT_SIZE,
    fullWidth,
    disabled,
    isLoading,
    leftIcon,
    rightIcon,
    as = "button",
    type,
    ...rest
  }, ref) => {
    const hasLeftIcon = leftIcon !== void 0;
    const hasRightIcon = rightIcon !== void 0;
    const iconProps = iconSizes[size ?? DEFAULT_SIZE];
    const isDisabled = disabled || !!isLoading;
    const sharedProps = {
      className: cn(
        buttonVariants({
          variant,
          size,
          fullWidth,
          hasLeftIcon,
          hasRightIcon,
          isDisabled,
          className
        })
      ),
      ref,
      disabled: isDisabled,
      type: as === "button" ? type ? type : "button" : void 0,
      ...rest
    };
    return cloneElement(
      as === "a" ? /* @__PURE__ */ React.createElement("a", null) : /* @__PURE__ */ React.createElement("button", null),
      sharedProps,
      /* @__PURE__ */ React.createElement(React.Fragment, null, leftIcon && !isLoading ? cloneElement(leftIcon, { ...iconProps }) : null, isLoading ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Spinner, { className: "absolute" }), /* @__PURE__ */ React.createElement("span", { className: "opacity-0" }, children)) : /* @__PURE__ */ React.createElement(React.Fragment, null, children), rightIcon && !isLoading ? cloneElement(rightIcon, { ...iconProps }) : null)
    );
  }
);

export {
  buttonVariants,
  Button
};
